"use client";

import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import { ApiConfig, API_CUSTOMER_DEBT_ROUTER } from "../../../services/api-config";
import BranchService, { Branch } from "../../branch/services/branch-service";
import {
  CustomerDebtSummaryRow,
  CustomerDebtSummaryTotals,
  CustomerDebtOrder,
} from "../services/customer-debt-service";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import { API_CUSTOMER_ROUTER } from "../../../services/api-config";

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw?.list && Array.isArray(raw.list)) return raw.list;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const formatNumber = (n: number) => numberFmt.format(Number(n) || 0);
const formatCurrency = (n: number) => `${formatNumber(n)}đ`;

// DD/MM/YYYY — toLocaleDateString("vi-VN") không pad số 0 (11/8/2026)
const formatDate = (value?: string) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const CustomerDebtIndexPage: React.FC = () => {
  const [selectedBranchId, setSelectedBranchId] = useState<number | "">("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | "">("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [rows, setRows] = useState<CustomerDebtSummaryRow[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [serverSummary, setServerSummary] = useState<CustomerDebtSummaryTotals | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [branches, setBranches] = useState<Branch[]>([]);

  // Detail modal state
  const [showModal, setShowModal] = useState(false);
  const [modalCustomerName, setModalCustomerName] = useState("");
  const [modalOrders, setModalOrders] = useState<CustomerDebtOrder[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalCustomerId, setModalCustomerId] = useState(0);

  // Pay modal state
  const [payingDebtId, setPayingDebtId] = useState<number | null>(null);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payLoading, setPayLoading] = useState(false);

  const { request: fetchSummary } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const { request: fetchCustomerOrders } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const { request: postPay } = useFetchData.Post<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const { fetch: listBranches } = BranchService.listActive();

  const fetchRef = useRef(fetchSummary);
  const ordersRef = useRef(fetchCustomerOrders);
  const payRef = useRef(postPay);
  const branchRef = useRef(listBranches);

  useEffect(() => { fetchRef.current = fetchSummary; }, [fetchSummary]);
  useEffect(() => { ordersRef.current = fetchCustomerOrders; }, [fetchCustomerOrders]);
  useEffect(() => { payRef.current = postPay; }, [postPay]);
  useEffect(() => { branchRef.current = listBranches; }, [listBranches]);

  // Load branches
  useEffect(() => {
    (async () => {
      const res = await branchRef.current({ status: 1, company_id: 2000 });
      if (res && isSuccess(res)) setBranches(extractList(res.data));
    })();
  }, []);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: pageSize };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      if (selectedCustomerId) params.customer_id = selectedCustomerId;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await fetchRef.current({
        endPoint: API_CUSTOMER_DEBT_ROUTER.API_SUMMARY,
        data: params,
      });
      if (res && isSuccess(res)) {
        setRows(res.data?.list ?? []);
        setTotalRecord(res.data?.total_record ?? 0);
        setServerSummary(res.data?.summary ?? null);
      }
    } catch (e) {
      console.error("Failed to load customer debt summary:", e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBranchId, selectedCustomerId, statusFilter, page, pageSize]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [selectedBranchId, selectedCustomerId, statusFilter]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  // Load customer orders helper
  const loadCustomerOrders = async (customerId: number) => {
    setModalLoading(true);
    try {
      const res = await ordersRef.current({
        endPoint: API_CUSTOMER_DEBT_ROUTER.API_CUSTOMER_ORDERS,
        data: { customer_id: customerId },
      });
      if (res && isSuccess(res)) {
        setModalOrders(extractList(res.data));
      }
    } catch (e) {
      console.error("Failed to load customer orders:", e);
    } finally {
      setModalLoading(false);
    }
  };

  // Open detail modal
  const openDetail = async (row: CustomerDebtSummaryRow) => {
    setShowModal(true);
    setModalCustomerName(row.customer_name);
    setModalCustomerId(row.customer_id);
    setModalOrders([]);
    setPayingDebtId(null);
    await loadCustomerOrders(row.customer_id);
  };

  // Pay debt
  const handlePayDebt = async () => {
    if (!payingDebtId || !payAmount) return;
    setPayLoading(true);
    try {
      const amount = Number(payAmount.replace(/[^0-9]/g, ""));
      if (!amount || amount <= 0) {
        alert("Số tiền không hợp lệ");
        return;
      }
      const res = await payRef.current({
        endPoint: API_CUSTOMER_DEBT_ROUTER.API_PAY(payingDebtId),
        data: { amount, note: payNote },
      });
      if (res && isSuccess(res)) {
        alert(res.data?.message || "Thanh toán thành công");
        setPayingDebtId(null);
        setPayAmount("");
        setPayNote("");
        // Reload danh sách đơn + summary
        await loadCustomerOrders(modalCustomerId);
        void loadReport();
      } else {
        alert(res?.message || "Thanh toán thất bại");
      }
    } catch (e: any) {
      alert(e?.message || "Lỗi thanh toán");
    } finally {
      setPayLoading(false);
    }
  };

  // Summary cards from server
  const summary = useMemo(() => {
    if (!serverSummary) return { total_debt: 0, total_paid: 0, remaining_debt: 0 };
    return serverSummary;
  }, [serverSummary]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(totalRecord / pageSize));

  const getDebtStatusBadge = (order: CustomerDebtOrder) => {
    if (order.paid_amount >= order.debt_amount) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Đã thanh toán</span>;
    }
    if (order.paid_amount > 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">Trả 1 phần</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Đang nợ</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Công nợ khách hàng</h1>
              <p className="text-sm text-gray-500 mt-0.5">Quản lý và theo dõi công nợ theo từng khách hàng</p>
            </div>
          </div>
          <button
            onClick={loadReport}
            disabled={isLoading}
            className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <svg className={`w-5 h-5 text-gray-600 ${isLoading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Chi nhánh</label>
              <select
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : "")}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-colors"
              >
                <option value="">Tất cả chi nhánh</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Khách hàng</label>
              <SelectDropdownAsyncPaginate
                url={API_CUSTOMER_ROUTER.API_LIST}
                projectId={ApiConfig.PROJECT_ID.CUSTOMER_SERVICE}
                params={{ company_id: 2000 }}
                placeholder="Tất cả khách hàng"
                enableSelectAll={true}
                selectAllLabel="Tất cả khách hàng"
                labelOption="name"
                classNames="w-full rounded-xl outline-none bg-white transition-colors border-0 p-0"
                onChange={(selected: any[]) => {
                  const item = selected[0];
                  setSelectedCustomerId(item && item.id !== -1 ? item.id : "");
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Trạng thái</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-colors"
              >
                <option value="all">Tất cả</option>
                <option value="in_debt">Đang nợ</option>
                <option value="paid">Đã thanh toán</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Tổng nợ</p>
              <p className="text-xl font-bold text-red-600 mt-0.5">{formatCurrency(summary.total_debt)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Đã thanh toán</p>
              <p className="text-xl font-bold text-green-600 mt-0.5">{formatCurrency(summary.total_paid)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Còn nợ</p>
              <p className="text-xl font-bold text-orange-600 mt-0.5">{formatCurrency(summary.remaining_debt)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Chi tiết công nợ theo khách hàng</h2>
          <span className="text-sm text-gray-400">{totalRecord} khách hàng</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12">#</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 min-w-[160px]">Khách hàng</th>
                <th className="px-4 py-3 text-right font-semibold text-red-600">Tổng nợ</th>
                <th className="px-4 py-3 text-right font-semibold text-green-600">Đã trả</th>
                <th className="px-4 py-3 text-right font-semibold text-orange-600 bg-orange-50/50">Còn nợ</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Số đơn</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-400 text-sm">Đang tải...</span>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">Không có dữ liệu</td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr
                    key={row.customer_id}
                    onClick={() => openDetail(row)}
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-center text-gray-400">{(page - 1) * pageSize + i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{row.customer_name}</td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium">{formatCurrency(row.total_debt)}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium">{formatCurrency(row.total_paid)}</td>
                    <td className="px-4 py-3 text-right font-bold text-orange-700 bg-orange-50/30">{formatCurrency(row.remaining_debt)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        {row.order_count}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalRecord > pageSize && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Hiển thị {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalRecord)} / {totalRecord}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                &laquo;
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                &lsaquo;
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | string)[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  typeof p === "string" ? (
                    <span key={`dot-${idx}`} className="px-1 text-gray-400">&hellip;</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        p === page
                          ? "bg-blue-500 text-white border-blue-500"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                &rsaquo;
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Chi tiết công nợ</h3>
                <p className="text-sm text-gray-500 mt-0.5">{modalCustomerName}</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {modalLoading ? (
                <div className="flex flex-col items-center gap-2 py-12">
                  <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-gray-400 text-sm">Đang tải...</span>
                </div>
              ) : modalOrders.length === 0 ? (
                <p className="text-center text-gray-400 py-12">Không có dữ liệu đơn hàng</p>
              ) : (
                <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-3 py-2.5 text-left font-semibold text-gray-600">Mã đơn</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-gray-600">Số nợ</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-gray-600">Đã trả</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-orange-600">Còn nợ</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-gray-600">Trạng thái</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-gray-600">Ngày tạo</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-gray-600">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {modalOrders.map((order) => {
                      const remaining = order.debt_amount - order.paid_amount;
                      const isPaying = payingDebtId === order.id;
                      return (
                        <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-3 py-2.5 font-medium text-gray-900">
                            <div>{order.order_code}</div>
                            <div className="text-xs text-gray-400">{formatDate(order.created_at)}</div>
                          </td>
                          <td className="px-3 py-2.5 text-right text-red-600">{formatCurrency(order.debt_amount)}</td>
                          <td className="px-3 py-2.5 text-right text-green-600">{formatCurrency(order.paid_amount)}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-orange-600">{remaining > 0 ? formatCurrency(remaining) : "-"}</td>
                          <td className="px-3 py-2.5 text-center">{getDebtStatusBadge(order)}</td>
                          <td className="px-3 py-2.5 text-center">
                            {remaining > 0 ? (
                              <button
                                onClick={() => {
                                  setPayingDebtId(isPaying ? null : order.id);
                                  setPayAmount(remaining.toString());
                                  setPayNote("");
                                }}
                                className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                                  isPaying ? "bg-gray-200 text-gray-600" : "bg-blue-500 text-white hover:bg-blue-600"
                                }`}
                              >
                                {isPaying ? "Huỷ" : "Thanh toán"}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pay form */}
                {payingDebtId && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3">Ghi nhận thanh toán</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Số tiền thanh toán</label>
                        <input
                          type="text"
                          value={formatNumber(Number(payAmount.replace(/[^0-9]/g, "")))}
                          onChange={(e) => setPayAmount(e.target.value.replace(/[^0-9]/g, ""))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="Nhập số tiền"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Ghi chú</label>
                        <input
                          type="text"
                          value={payNote}
                          onChange={(e) => setPayNote(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="Ghi chú (tuỳ chọn)"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={handlePayDebt}
                          disabled={payLoading}
                          className="w-full bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50 transition-colors"
                        >
                          {payLoading ? "Đang xử lý..." : "Xác nhận thanh toán"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDebtIndexPage;
